import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from './ui/basicos';
import { Campo } from './ui/formulario';
import { useTextoDeError } from './ui/estados';
import { useSesion } from '../sesion/ProveedorDeSesion';

/**
 * Cambiar la propia clave: la actual y la nueva, dos veces.
 *
 * Es un solo formulario para dos lugares —el diálogo del menú y la pantalla «Elegí tu clave» con
 * la que se entra tras una provisoria— porque la regla es una: sin la actual, no. Una sesión
 * abierta en una máquina compartida no puede ser la puerta para quedarse con la cuenta.
 *
 * Al terminar recarga el perfil: es lo que saca la marca de «debe cambiar» de la pantalla.
 */
export function FormularioDeClave({
  onListo,
}: {
  readonly onListo: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const textoDeError = useTextoDeError();
  const { recargarPerfil } = useSesion();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cambiar = useMutation({
    mutationFn: () => pedir<void>('/acceso/clave', { method: 'PATCH', cuerpo: { actual, nueva } }),
    onSuccess: async () => {
      setError(null);
      await recargarPerfil();
      onListo();
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const coinciden = nueva === repetida;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!coinciden) {
          setError(t('clave.noCoinciden'));
          return;
        }
        cambiar.mutate();
      }}
    >
      <Campo
        etiqueta={t('clave.actual')}
        type="password"
        autoComplete="current-password"
        obligatorio
        value={actual}
        onChange={(evento) => {
          setActual(evento.target.value);
        }}
      />
      <Campo
        etiqueta={t('clave.nueva')}
        ayuda={t('clave.ayudaNueva')}
        type="password"
        autoComplete="new-password"
        obligatorio
        value={nueva}
        onChange={(evento) => {
          setNueva(evento.target.value);
        }}
      />
      <Campo
        etiqueta={t('clave.repetir')}
        type="password"
        autoComplete="new-password"
        obligatorio
        value={repetida}
        onChange={(evento) => {
          setRepetida(evento.target.value);
        }}
      />
      {error === null ? null : (
        <p role="alert" className="text-xs text-mal">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <Boton tono="principal" type="submit" cargando={cambiar.isPending}>
          {t('clave.cambiar')}
        </Boton>
      </div>
    </form>
  );
}
